import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { userAnalytics, TransactionHistory, UserLifetimeStats } from '../services/userAnalytics';
import LoadingSpinner from '../components/LoadingSpinner';
import { useTranslation } from 'react-i18next';
import {
  CurrencyRupeeIcon,
  ClockIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  CalendarDaysIcon,
  ArrowDownTrayIcon,
  ChartBarIcon,
  ClipboardDocumentIcon
} from '@heroicons/react/24/outline';

const Transactions: React.FC = React.memo(() => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [transactionHistory, setTransactionHistory] = useState<TransactionHistory | null>(null);
  const [lifetimeStats, setLifetimeStats] = useState<UserLifetimeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'cashback' | 'withdrawal'>('all');
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'week' | 'month' | 'year' | 'custom'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'pending' | 'failed' | 'rejected'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  // Track screen width for responsive adjustments
  useEffect(() => {
    const updateScreenWidth = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener('resize', updateScreenWidth);
    return () => window.removeEventListener('resize', updateScreenWidth);
  }, []);

  // Helper function to check if two dates are the same day
  const isSameDay = (date1: Date, date2: Date) => {
    return date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear();
  };

  const fetchTransactionHistory = useCallback(async () => {
    if (!user || transactionHistory) return; // Don't fetch if already loaded

    try {
      const history = await userAnalytics.getTransactionHistory(user.id);

      setTransactionHistory(history);
      setLifetimeStats(history.lifetimeStats);
    } catch (error) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [user, transactionHistory]);

  useEffect(() => {
    fetchTransactionHistory();
  }, [fetchTransactionHistory]);

  // Merge transactions and payment requests into one list
  const getAllTransactions = () => {
    if (!transactionHistory) return [];

    // Filter out transaction records that are linked to payment requests
    const transactions = transactionHistory.transactions
      .filter(trx => !trx.paymentRequestId) // Exclude transactions created by payment approvals
      .map(trx => {
        // Translate description if it contains "Coupon redemption"
        let description = (trx as any).description || '';
        let couponCode = (trx as any).couponCode || '';

        // Extract coupon code from description if it's in format "Coupon redemption: [CODE]"
        if (description.toLowerCase().includes('coupon redemption')) {
          // Try to extract coupon code from description
          const match = description.match(/coupon redemption:\s*([A-Z0-9-]+)/i);
          if (match && match[1]) {
            couponCode = match[1].trim();
          }
          description = t('transactions.couponRedemption');
        } else if (!description && trx.type === 'cashback') {
          // If no description and it's a cashback transaction, use translated text
          description = t('transactions.couponRedemption');
        }

        return {
          ...trx,
          description,
          couponCode: couponCode || (trx as any).couponCode || '',
          productName: (trx as any).productName || '',
          isPaymentRequest: false
        };
      });

    const paymentRequests = transactionHistory.paymentRequests.map(pr => {
      let status = 'pending';
      if (pr.status === 'approved') {
        status = 'processing';
      } else if (pr.status === 'paid') {
        status = 'paid';
      } else if (pr.status === 'rejected') {
        status = 'rejected';
      }

      // Debug: Log transactionId for payment requests
      if (pr.status === 'approved' || pr.status === 'paid') {
        console.log(`Payment Request ${pr.id} (${pr.status}): transactionId =`, pr.transactionId);
      }

      // Calculate correct received amount
      const tdsAmount = pr.tdsAmount || 0;
      const processingFee = pr.processingFee || (pr.amount * 0.05); // Default 5% if missing legacy
      const netAmount = pr.netAmount || (pr.amount - processingFee - tdsAmount);

      const receivedText = t('transactions.received', { amount: netAmount.toFixed(2) });

      let descriptionSuffix = ` (${receivedText})`;

      return {
        id: pr.id,
        userId: pr.userId,
        amount: pr.amount, // Show original requested amount
        type: 'withdrawal' as const,
        status,
        createdAt: pr.createdAt,
        description: t('transactions.paymentRequest', { amount: pr.amount }) + descriptionSuffix,
        isPaymentRequest: true,
        originalStatus: pr.status,
        specificRejectReason: pr.specificRejectReason,
        transactionId: pr.transactionId || undefined, // Include transaction ID from payment request
        // Pass through financial details for UI if needed later
        tdsAmount,
        processingFee,
        netAmount
      };
    });

    return [...transactions, ...paymentRequests].sort((a, b) =>
      b.createdAt.getTime() - a.createdAt.getTime()
    );
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Enhanced filtering logic
  const filteredTransactions = getAllTransactions().filter(transaction => {
    // Type filter (cashback/withdrawal)
    if (filter !== 'all' && transaction.type !== filter) return false;

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const transactionDate = transaction.createdAt;

      switch (dateFilter) {
        case 'today':
          if (!isSameDay(transactionDate, now)) return false;
          break;
        case 'week':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          if (transactionDate < weekAgo) return false;
          break;
        case 'month':
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          if (transactionDate < monthAgo) return false;
          break;
        case 'year':
          const yearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
          if (transactionDate < yearAgo) return false;
          break;
        case 'custom':
          if (customDateRange.startDate && customDateRange.endDate) {
            const startDate = new Date(customDateRange.startDate);
            const endDate = new Date(customDateRange.endDate);
            endDate.setHours(23, 59, 59, 999); // Include full end date

            if (transactionDate < startDate || transactionDate > endDate) {
              return false;
            }
          }
          break;
      }
    }

    // Status filter
    if (statusFilter !== 'all') {
      if (transaction.isPaymentRequest) {
        switch (statusFilter) {
          case 'completed':
            if (transaction.status !== 'paid') return false;
            break;
          case 'pending':
            if (!['pending', 'processing'].includes(transaction.status)) return false;
            break;
          case 'failed':
          case 'rejected':
            if (transaction.status !== 'rejected') return false;
            break;
        }
      } else {
        switch (statusFilter) {
          case 'completed':
            if (transaction.status !== 'completed') return false;
            break;
          case 'pending':
            if (transaction.status !== 'pending') return false;
            break;
          case 'failed':
            if (transaction.status !== 'failed') return false;
            break;
          case 'rejected':
            return false; // Regular transactions don't have rejected status
        }
      }
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchesAmount = transaction.amount.toString().includes(query);
      const matchesDescription = (transaction as any).description?.toLowerCase().includes(query) || false;
      const matchesStatus = transaction.status.toLowerCase().includes(query);
      const matchesDate = formatDate(transaction.createdAt).toLowerCase().includes(query);

      if (!matchesAmount && !matchesDescription && !matchesStatus && !matchesDate) {
        return false;
      }
    }

    return true;
  });

  // Get filter summary text
  const getFilterSummary = () => {
    const filters = [];
    if (filter !== 'all') filters.push(filter === 'cashback' ? t('transactions.earnings') : t('transactions.withdrawals'));
    if (dateFilter !== 'all') {
      if (dateFilter === 'custom') {
        if (customDateRange.startDate && customDateRange.endDate) {
          const start = new Date(customDateRange.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
          const end = new Date(customDateRange.endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
          filters.push(`${start} - ${end}`);
        } else {
          filters.push(t('transactions.customRange'));
        }
      } else {
        const dateLabels = {
          today: t('transactions.today'),
          week: t('transactions.thisWeek'),
          month: t('transactions.thisMonth'),
          year: t('transactions.thisYear')
        };
        filters.push(dateLabels[dateFilter]);
      }
    }
    if (statusFilter !== 'all') {
      const statusLabels = {
        completed: t('transactions.completed'),
        pending: t('transactions.pending'),
        failed: t('transactions.failed'),
        rejected: t('transactions.rejected')
      };
      filters.push(statusLabels[statusFilter]);
    }
    return filters.length > 0 ? `${t('transactions.filteredBy')} ${filters.join(', ')}` : t('transactions.allTransactions');
  };

  const formatDateGroup = (date: Date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return t('transactions.today');
    } else if (date.toDateString() === yesterday.toDateString()) {
      return t('transactions.yesterday');
    } else {
      return date.toLocaleDateString('en-IN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  const groupTransactionsByDate = (transactions: any[]) => {
    const groups: { [key: string]: any[] } = {};

    transactions.forEach(transaction => {
      const dateGroup = formatDateGroup(transaction.createdAt);
      if (!groups[dateGroup]) {
        groups[dateGroup] = [];
      }
      groups[dateGroup].push(transaction);
    });

    return groups;
  };

  const getStatusColor = (status: string, isPaymentRequest?: boolean) => {
    if (isPaymentRequest) {
      switch (status) {
        case 'pending':
          return '#f59e0b'; // Orange for pending
        case 'processing':
          return '#ef4444'; // Red for processing
        case 'paid':
          return '#10b981'; // Green for paid
        case 'rejected':
          return '#ef4444'; // Red for rejected
        default:
          return '#6b7280';
      }
    }

    switch (status) {
      case 'pending':
        return '#f59e0b'; // Orange for pending
      case 'completed':
        return '#3b82f6'; // Blue for cashback
      case 'failed':
        return '#ef4444'; // Red for failed
      default:
        return '#6b7280';
    }
  };

  const getStatusText = (status: string, isPaymentRequest?: boolean, transactionType?: string) => {
    if (isPaymentRequest) {
      switch (status) {
        case 'pending':
          return t('transactions.pending');
        case 'processing':
          return t('transactions.processing');
        case 'paid':
          return t('transactions.paid');
        case 'rejected':
          return t('transactions.rejected');
        default:
          return status;
      }
    }

    switch (status) {
      case 'pending':
        return t('transactions.pending');
      case 'completed':
        return transactionType === 'cashback' ? t('transactions.cashback') : t('transactions.completed');
      case 'failed':
        return t('transactions.failed');
      default:
        return status;
    }
  };

  const getStatusIcon = (status: string, isPaymentRequest?: boolean) => {
    if (isPaymentRequest) {
      switch (status) {
        case 'pending':
          return <ClockIcon style={{ width: '16px', height: '16px' }} />;
        case 'processing':
          return <ClockIcon style={{ width: '16px', height: '16px' }} />;
        case 'paid':
          return <CheckCircleIcon style={{ width: '16px', height: '16px' }} />;
        case 'rejected':
          return <XCircleIcon style={{ width: '16px', height: '16px' }} />;
        default:
          return <ExclamationTriangleIcon style={{ width: '16px', height: '16px' }} />;
      }
    }

    switch (status) {
      case 'pending':
        return <ClockIcon style={{ width: '16px', height: '16px' }} />;
      case 'completed':
        return <CheckCircleIcon style={{ width: '16px', height: '16px' }} />;
      case 'failed':
        return <XCircleIcon style={{ width: '16px', height: '16px' }} />;
      default:
        return <ExclamationTriangleIcon style={{ width: '16px', height: '16px' }} />;
    }
  };

  if (!user) {
    return <LoadingSpinner />;
  }

  // Full-screen responsive container style
  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    width: '100vw',
    background: '#eef3f9',
    paddingTop: '0px',
    paddingBottom: 'calc(env(safe-area-inset-bottom) + 80px)',
    margin: 0,
    overflowX: 'hidden' as React.CSSProperties['overflowX'],
    overflowY: 'auto' as React.CSSProperties['overflowY']
  };

  const headerStyle = {
    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
    borderRadius: '20px',
    padding: '24px',
    marginBottom: '20px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    border: '2px solid rgba(0, 0, 0, 0.05)',
    textAlign: 'center' as const,
    backdropFilter: 'blur(10px)'
  };

  const titleStyle = {
    fontSize: '28px',
    fontWeight: '800',
    color: '#1f2937',
    marginBottom: '8px',
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
  };

  const subtitleStyle = {
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '16px',
    fontWeight: '500'
  };

  const statsContainerStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginTop: '20px'
  };

  const statCardStyle = (cardType: string) => ({
    background: cardType === 'earned'
      ? 'linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)'
      : cardType === 'withdrawn'
        ? 'linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)'
        : cardType === 'balance'
          ? 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)'
          : 'linear-gradient(135deg, #ffffff 0%, #fef3c7 100%)',
    borderRadius: '16px',
    padding: '16px',
    textAlign: 'center' as const,
    border: cardType === 'earned'
      ? '2px solid rgba(59, 130, 246, 0.3)'
      : cardType === 'withdrawn'
        ? '2px solid rgba(239, 68, 68, 0.3)'
        : cardType === 'balance'
          ? '2px solid rgba(16, 185, 129, 0.3)'
          : '2px solid rgba(245, 158, 11, 0.3)',
    boxShadow: cardType === 'earned'
      ? '0 4px 16px rgba(59, 130, 246, 0.2)'
      : cardType === 'withdrawn'
        ? '0 4px 16px rgba(239, 68, 68, 0.2)'
        : cardType === 'balance'
          ? '0 4px 16px rgba(16, 185, 129, 0.2)'
          : '0 4px 16px rgba(245, 158, 11, 0.2)',
    transition: 'all 0.3s ease'
  });

  const statValueStyle = (cardType: string) => ({
    fontSize: '22px',
    fontWeight: '800',
    color: cardType === 'earned'
      ? '#3b82f6'
      : cardType === 'withdrawn'
        ? '#ef4444'
        : cardType === 'balance'
          ? '#10b981'
          : '#f59e0b',
    marginBottom: '4px'
  });

  const statLabelStyle = (cardType: string) => ({
    fontSize: '12px',
    color: cardType === 'earned'
      ? '#1e40af'
      : cardType === 'withdrawn'
        ? '#dc2626'
        : cardType === 'balance'
          ? '#059669'
          : '#d97706',
    fontWeight: '600'
  });

  const sectionStyle = {
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)',
    borderRadius: '20px',
    padding: '20px',
    marginBottom: '20px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    border: '2px solid rgba(0, 0, 0, 0.05)',
    backdropFilter: 'blur(10px)'
  };

  const sectionTitleStyle = {
    fontSize: '20px',
    fontWeight: '800',
    color: '#1f2937',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const filterContainerStyle = {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px'
  };

  const filterButtonStyle = (active: boolean) => ({
    flex: 1,
    padding: '10px 14px',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '700',
    border: active ? '2px solid #1f2937' : '2px solid rgba(0, 0, 0, 0.1)',
    cursor: 'pointer',
    background: active
      ? 'linear-gradient(135deg, #1f2937 0%, #374151 100%)'
      : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
    color: active ? 'white' : '#1f2937',
    transition: 'all 0.3s ease',
    boxShadow: active
      ? '0 4px 16px rgba(0, 0, 0, 0.2)'
      : '0 2px 8px rgba(0, 0, 0, 0.1)',
    transform: active ? 'translateY(-1px)' : 'translateY(0)'
  });

  const transactionCardStyle = (transactionType: string, status: string) => ({
    background: transactionType === 'cashback'
      ? 'linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)'
      : status === 'pending' || status === 'processing'
        ? 'linear-gradient(135deg, #ffffff 0%, #fef3c7 100%)'
        : status === 'paid' || status === 'completed'
          ? 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)'
          : status === 'rejected' || status === 'failed'
            ? 'linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
    borderRadius: '0px',
    padding: '18px',
    marginBottom: '12px',
    border: transactionType === 'cashback'
      ? '2px solid rgba(59, 130, 246, 0.3)'
      : status === 'pending' || status === 'processing'
        ? '2px solid rgba(245, 158, 11, 0.3)'
        : status === 'paid' || status === 'completed'
          ? '2px solid rgba(16, 185, 129, 0.3)'
          : status === 'rejected' || status === 'failed'
            ? '2px solid rgba(239, 68, 68, 0.3)'
            : '2px solid rgba(0, 0, 0, 0.1)',
    boxShadow: transactionType === 'cashback'
      ? '0 4px 16px rgba(59, 130, 246, 0.2)'
      : status === 'pending' || status === 'processing'
        ? '0 4px 16px rgba(245, 158, 11, 0.2)'
        : status === 'paid' || status === 'completed'
          ? '0 4px 16px rgba(16, 185, 129, 0.2)'
          : status === 'rejected' || status === 'failed'
            ? '0 4px 16px rgba(239, 68, 68, 0.2)'
            : '0 4px 16px rgba(0, 0, 0, 0.1)',
    position: 'relative' as const,
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    backdropFilter: 'blur(10px)'
  });

  const transactionCardGradient = (transactionType: string, status: string) => ({
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: transactionType === 'cashback'
      ? 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)'
      : status === 'pending' || status === 'processing'
        ? 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)'
        : status === 'paid' || status === 'completed'
          ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
          : status === 'rejected' || status === 'failed'
            ? 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)'
            : 'linear-gradient(90deg, #1f2937 0%, #374151 100%)',
    borderRadius: '16px 16px 0 0'
  });

  const transactionHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px'
  };

  const transactionAmountStyle = (transactionType: string, status: string) => ({
    fontSize: '20px',
    fontWeight: '800',
    color: transactionType === 'cashback'
      ? '#3b82f6'
      : status === 'pending' || status === 'processing'
        ? '#f59e0b'
        : status === 'paid' || status === 'completed'
          ? '#10b981'
          : status === 'rejected' || status === 'failed'
            ? '#ef4444'
            : '#1f2937'
  });

  const statusStyle = (status: string, isPaymentRequest?: boolean) => ({
    padding: '6px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '700',
    background: `linear-gradient(135deg, ${getStatusColor(status, isPaymentRequest)}20 0%, ${getStatusColor(status, isPaymentRequest)}10 100%)`,
    color: getStatusColor(status, isPaymentRequest),
    border: `2px solid ${getStatusColor(status, isPaymentRequest)}30`,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    boxShadow: `0 2px 8px ${getStatusColor(status, isPaymentRequest)}20`
  });

  const transactionDetailsStyle = {
    fontSize: '14px',
    color: '#6b7280',
    lineHeight: '1.4',
    fontWeight: '500'
  };

  const dateGroupStyle = {
    fontSize: '18px',
    fontWeight: '800',
    color: '#1f2937',
    marginBottom: '12px',
    paddingBottom: '8px',
    borderBottom: '2px solid rgba(0, 0, 0, 0.1)'
  };

  const emptyStateStyle = {
    textAlign: 'center' as const,
    padding: '40px 20px',
    color: '#6b7280'
  };

  const emptyIconStyle = {
    fontSize: '48px',
    marginBottom: '16px',
    opacity: '0.5'
  };

  const loadingStyle = {
    textAlign: 'center' as const,
    padding: '40px 20px',
    color: '#666'
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const groupedTransactions = groupTransactionsByDate(filteredTransactions);

  const exportTransactionsToCSV = () => {
    if (!filteredTransactions.length) {
      return;
    }

    const headers = [
      'Type',
      'Amount',
      'Status',
      'Description',
      'Coupon Code',
      'Is Payment Request',
      'Original Status',
      'Specific Reject Reason',
      'Transaction ID',
      'Created At'
    ];

    const rows = filteredTransactions.map((transaction: any) => {
      const createdAt = transaction.createdAt instanceof Date
        ? transaction.createdAt.toISOString()
        : transaction.createdAt;

      return [
        transaction.type || '',
        transaction.amount != null ? transaction.amount : '',
        transaction.status || '',
        transaction.description || '',
        transaction.couponCode || '',
        transaction.isPaymentRequest ? 'yes' : 'no',
        transaction.originalStatus || '',
        transaction.specificRejectReason || '',
        transaction.transactionId || '',
        createdAt || ''
      ];
    });

    const escapeCSV = (value: any) => {
      const str = value == null ? '' : String(value);
      const escaped = str.replace(/"/g, '""');
      return `"${escaped}"`;
    };

    const csvContent =
      [headers, ...rows]
        .map(row => row.map(escapeCSV).join(','))
        .join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const dateStr = new Date().toISOString().slice(0, 10);

    link.href = url;
    link.setAttribute('download', `transactions_${dateStr}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className={screenWidth >= 768 ? 'hide-scrollbar-desktop' : ''}
      style={containerStyle}
    >
      <div
        style={{
          width: '100%',
          paddingTop: screenWidth < 768 ? '16px' : '24px',
          paddingBottom: screenWidth < 768 ? '16px' : '24px',
          paddingLeft: '0px',
          paddingRight: '0px',
          maxWidth: '100%'
        }}
      >
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ marginBottom: '16px' }}>
            <h1 style={titleStyle}>{t('transactions.title')}</h1>
            <p style={subtitleStyle}>{getFilterSummary()}</p>
          </div>

          {lifetimeStats && (
            <div style={statsContainerStyle}>
              <div style={statCardStyle('earned')}>
                <div style={statValueStyle('earned')}>₹{lifetimeStats.totalEarned}</div>
                <div style={statLabelStyle('earned')}>{t('transactions.totalEarned')}</div>
              </div>
              <div style={statCardStyle('withdrawn')}>
                <div style={statValueStyle('withdrawn')}>₹{lifetimeStats.totalWithdrawn}</div>
                <div style={statLabelStyle('withdrawn')}>{t('transactions.totalWithdrawn')}</div>
              </div>
              <div style={statCardStyle('balance')}>
                <div style={statValueStyle('balance')}>₹{lifetimeStats.availableBalance}</div>
                <div style={statLabelStyle('balance')}>{t('transactions.availableBalance')}</div>
              </div>
              <div style={statCardStyle('transactions')}>
                <div style={statValueStyle('transactions')}>{lifetimeStats.totalTransactions}</div>
                <div style={statLabelStyle('transactions')}>{t('transactions.totalTransactions')}</div>
              </div>
            </div>
          )}
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div style={sectionStyle}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h3 style={{ ...sectionTitleStyle, marginBottom: 0 }}>
                <FunnelIcon style={{ width: '20px', height: '20px' }} />
                {t('transactions.filters')}
              </h3>
              <button
                onClick={() => setShowFilters(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6b7280',
                  fontSize: '20px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(107, 114, 128, 0.1)';
                  e.currentTarget.style.color = '#374151';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#6b7280';
                }}
              >
                ×
              </button>
            </div>

            {/* Search */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ position: 'relative' }}>
                <MagnifyingGlassIcon style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '18px',
                  height: '18px',
                  color: '#999'
                }} />
                <input
                  type="text"
                  placeholder={t('transactions.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 40px',
                    border: '2px solid rgba(0, 0, 0, 0.1)',
                    borderRadius: '12px',
                    fontSize: '14px',
                    backgroundColor: '#ffffff',
                    transition: 'all 0.3s ease',
                    color: '#1f2937',
                    fontWeight: '500'
                  }}
                />
              </div>
            </div>

            {/* Type Filter */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '8px', display: 'block' }}>
                {t('transactions.transactionType')}
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                {[
                  { key: 'all', label: t('transactions.all'), icon: ChartBarIcon },
                  { key: 'cashback', label: t('transactions.earned'), icon: ArrowDownIcon },
                  { key: 'withdrawal', label: t('transactions.withdrawn'), icon: ArrowUpIcon }
                ].map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => setFilter(key as any)}
                    style={filterButtonStyle(filter === key)}
                  >
                    <Icon style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Date Filter */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '8px', display: 'block' }}>
                <CalendarDaysIcon style={{ width: '16px', height: '16px', marginRight: '4px', display: 'inline' }} />
                {t('transactions.dateRange')}
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {[
                  { key: 'all', label: t('transactions.allTime') },
                  { key: 'today', label: t('transactions.today') },
                  { key: 'week', label: t('transactions.thisWeek') },
                  { key: 'month', label: t('transactions.thisMonth') },
                  { key: 'custom', label: t('transactions.customRange') }
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setDateFilter(key as any)}
                    style={filterButtonStyle(dateFilter === key)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Custom Date Range Inputs */}
              {dateFilter === 'custom' && (
                <div style={{
                  marginTop: '12px',
                  padding: '16px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '12px',
                  border: '2px solid #e9ecef'
                }}>
                  <label style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '12px', display: 'block' }}>
                    {t('transactions.selectDateRange')}
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>
                        {t('transactions.startDate')}
                      </label>
                      <input
                        type="date"
                        value={customDateRange.startDate}
                        onChange={(e) => setCustomDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '2px solid rgba(0, 0, 0, 0.1)',
                          borderRadius: '8px',
                          fontSize: '14px',
                          backgroundColor: '#ffffff',
                          color: '#1f2937',
                          fontWeight: '500'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>
                        {t('transactions.endDate')}
                      </label>
                      <input
                        type="date"
                        value={customDateRange.endDate}
                        onChange={(e) => setCustomDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '2px solid rgba(0, 0, 0, 0.1)',
                          borderRadius: '8px',
                          fontSize: '14px',
                          backgroundColor: '#ffffff',
                          color: '#1f2937',
                          fontWeight: '500'
                        }}
                      />
                    </div>
                  </div>
                  {customDateRange.startDate && customDateRange.endDate && (
                    <div style={{
                      marginTop: '8px',
                      fontSize: '12px',
                      color: '#28a745',
                      fontWeight: '600'
                    }}>
                      ✓ {t('transactions.showingTransactions')} {new Date(customDateRange.startDate).toLocaleDateString('en-IN')} {t('transactions.to')} {new Date(customDateRange.endDate).toLocaleDateString('en-IN')}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Status Filter */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '8px', display: 'block' }}>
                {t('transactions.status')}
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {[
                  { key: 'all', label: t('transactions.all'), icon: ChartBarIcon },
                  { key: 'completed', label: t('transactions.completed'), icon: CheckCircleIcon },
                  { key: 'pending', label: t('transactions.pending'), icon: ClockIcon },
                  { key: 'rejected', label: t('transactions.failedRejected'), icon: XCircleIcon }
                ].map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => setStatusFilter(key as any)}
                    style={filterButtonStyle(statusFilter === key)}
                  >
                    <Icon style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Clear Filters */}
            <button
              onClick={() => {
                setFilter('all');
                setDateFilter('all');
                setStatusFilter('all');
                setSearchQuery('');
                setCustomDateRange({ startDate: '', endDate: '' });
              }}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#ffffff',
                border: '2px solid rgba(0, 0, 0, 0.1)',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '700',
                color: '#1f2937',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
            >
              {t('transactions.clearAllFilters')}
            </button>
          </div>
        )}

        {/* Transactions Section */}
        <div style={sectionStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h2 style={sectionTitleStyle}>
              <ClockIcon style={{ width: '20px', height: '20px' }} />
              {t('transactions.transactions')} ({filteredTransactions.length})
            </h2>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setShowFilters(!showFilters)}
                style={{
                  backgroundColor: showFilters ? 'linear-gradient(135deg, #1f2937 0%, #374151 100%)' : '#ffffff',
                  border: showFilters ? '2px solid #1f2937' : '2px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: '12px',
                  padding: '12px',
                  cursor: 'pointer',
                  boxShadow: showFilters ? '0 4px 16px rgba(0, 0, 0, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                  transform: showFilters ? 'translateY(-1px)' : 'translateY(0)'
                }}
              >
                <FunnelIcon style={{ width: '20px', height: '20px', color: showFilters ? 'white' : '#666' }} />
              </button>
              <button
                onClick={exportTransactionsToCSV}
                style={{
                  backgroundColor: '#ffffff',
                  border: '2px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: '12px',
                  padding: '12px',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease'
                }}
              >
                <ArrowDownTrayIcon style={{ width: '20px', height: '20px', color: '#666' }} />
              </button>
            </div>
          </div>

          {/* Transactions List */}
          {Object.keys(groupedTransactions).length > 0 ? (
            Object.entries(groupedTransactions).map(([dateGroup, transactions]) => (
              <div key={dateGroup}>
                <div style={dateGroupStyle}>{dateGroup}</div>
                {transactions.map((transaction) => (
                  <div key={transaction.id} style={transactionCardStyle(transaction.type, transaction.status)}>
                    {/* Gradient top border */}
                    <div style={transactionCardGradient(transaction.type, transaction.status)} />

                    <div style={transactionHeaderStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <div style={transactionAmountStyle(transaction.type, transaction.status)}>
                          {transaction.type === 'cashback' ? '+' : '-'}₹{transaction.amount}
                        </div>
                        {transaction.type === 'cashback' && (transaction as any).pointsEarned && (transaction as any).pointsEarned > 0 && (
                          <div style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#10b981',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '2px'
                          }}>
                            <span>+</span>
                            <span>{(transaction as any).pointsEarned}</span>
                            <span style={{ fontSize: '12px', opacity: 0.8 }}>pts</span>
                          </div>
                        )}
                      </div>
                      <div style={statusStyle(transaction.status, transaction.isPaymentRequest)}>
                        {getStatusIcon(transaction.status, transaction.isPaymentRequest)}
                        {getStatusText(transaction.status, transaction.isPaymentRequest, transaction.type)}
                      </div>
                    </div>

                    <div style={transactionDetailsStyle}>
                      <div>
                        {transaction.type === 'cashback' && (transaction as any).productName ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <div style={transactionDetailsStyle}>
                              Token redeem successful
                            </div>
                            <div style={transactionDetailsStyle}>
                              Product: {(transaction as any).productName}
                            </div>
                          </div>
                        ) : transaction.type === 'cashback' && (transaction as any).couponCode ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <div style={transactionDetailsStyle}>
                              Token redeem successful
                            </div>
                            <div style={transactionDetailsStyle}>
                              Coupon: {(transaction as any).couponCode}
                            </div>
                          </div>
                        ) : transaction.type === 'cashback' ? (
                          <div style={transactionDetailsStyle}>
                            Token redeem successful
                          </div>
                        ) : (
                          transaction.description || ''
                        )}
                      </div>

                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '4px',
                        gap: '12px'
                      }}>
                        {/* Date and Time - Left Side */}
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          {formatDate(transaction.createdAt)}
                        </div>
                      </div>

                      {/* Transaction ID - Below date, aligned to right with copy icon */}
                      {(transaction as any).transactionId && (
                        <div style={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                          marginTop: '2px',
                          gap: '6px'
                        }}>
                          <div style={{
                            fontSize: '12px',
                            color: '#999'
                          }}>
                            {(transaction as any).transactionId}
                          </div>
                          <button
                            onClick={(e) => {
                              navigator.clipboard.writeText((transaction as any).transactionId);
                              e.stopPropagation();
                              const icon = e.currentTarget.querySelector('svg');
                              if (icon) {
                                icon.style.color = '#10b981';
                                setTimeout(() => {
                                  icon.style.color = '#999';
                                }, 1500);
                              }
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: '2px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <ClipboardDocumentIcon style={{
                              width: '14px',
                              height: '14px',
                              color: '#999',
                              transition: 'color 0.2s ease'
                            }} />
                          </button>
                        </div>
                      )}

                      {/* Show specific rejection reason for payment requests */}
                      {transaction.isPaymentRequest &&
                        transaction.originalStatus === 'rejected' &&
                        transaction.specificRejectReason && (
                          <div style={{
                            marginTop: '8px',
                            padding: '8px',
                            backgroundColor: '#fff5f5',
                            borderRadius: '6px',
                            border: '1px solid #fed7d7',
                            fontSize: '12px',
                            color: '#dc3545'
                          }}>
                            ❌ {transaction.specificRejectReason}
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            ))
          ) : (
            <div style={emptyStateStyle}>
              <div style={emptyIconStyle}>📊</div>
              <p>{t('transactions.noTransactionsFound')}</p>
              <p style={{ fontSize: '12px', color: '#999' }}>
                {filter === 'all'
                  ? t('transactions.transactionHistoryWillAppear')
                  : t('transactions.noTransactionsFoundFilter', { filter: filter === 'cashback' ? t('transactions.earnings') : t('transactions.withdrawals') })
                }
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default Transactions; 